import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardCheck, Check, Minus, TriangleAlert, ShieldCheck, Plus, Trash2,
  Radio, CircleDashed,
} from "lucide-react";
import { Pagina, Cartao, Carregando, Falha, Indicador } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { Modal } from "./Equipamentos.js";
import { trpc } from "../lib/trpc.js";
import { data as fData } from "../lib/formato.js";

type Item = { chave: string; titulo: string; entregue?: boolean; peso?: number };

const hoje = () => new Date().toISOString().slice(0, 10);

/**
 * Combinado do dia.
 *
 * A unidade do módulo. O objeto do contrato de trabalho é a prestação
 * combinada, não a presença diante da tela, então medir o combinado é medir
 * aquilo que a lei já protege. É por isso que este caminho não gera passivo, e
 * é por isso que ele é o primeiro a existir, antes de qualquer coletor.
 */
export function Atividade() {
  const [dia, setDia] = useState(hoje());
  const [combinandoPara, setCombinandoPara] = useState<string | null>(null);
  const [fechando, setFechando] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["atividade", "dia", dia],
    queryFn: () => trpc.atividade.doDia.query({ dia }),
  });
  const equipe = useQuery({
    queryKey: ["pessoas", "quadro"],
    queryFn: () => trpc.cadastro.pessoas.query({}),
  });
  const politica = useQuery({
    queryKey: ["atividade", "politica"],
    queryFn: () => trpc.atividade.politica.query(),
  });

  const combinados = q.data ?? [];
  const fechados = combinados.filter((c) => c.fechadoEm);
  const semCombinado = (equipe.data ?? []).filter(
    (m) => m.vinculoId && !combinados.some((c) => c.vinculoId === m.vinculoId));

  return (
    <Pagina
      secao="Atividade"
      titulo="Combinado do dia"
      descricao="O escopo acordado de manhã e o resultado à noite. Quem entrega o combinado está trabalhando, e essa é a pergunta que importa."
      acoes={
        <input type="date" className="eop-data-topo" value={dia}
               onChange={(e) => setDia(e.target.value)} aria-label="Dia" />
      }
    >
      {politica.isSuccess && !politica.data.vigente && (
        <p className="eop-erro" role="status">
          <ShieldCheck aria-hidden size={15} />
          <span>
            Nenhuma política de monitoramento publicada. O combinado do dia
            funciona assim mesmo, porque ele é gestão e não coleta. Coletor
            nenhum liga antes da política e da ciência assinada.
          </span>
        </p>
      )}

      {politica.isSuccess && politica.data.vigente
        && politica.data.semCiencia.length > 0 && (
        <Cartao
          titulo="Sem ciência da política vigente"
          subtitulo="Enquanto não derem ciência, nada é coletado dessas pessoas. É trava de código, não lembrete"
        >
          <ul className="eop-op">
            {politica.data.semCiencia.map((p) => (
              <li key={p.vinculoId} data-tom="atencao">
                <span aria-hidden className="eop-op-icone"><CircleDashed size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">{p.nome}</span>
                  <span className="eop-op-sub">matrícula {p.matricula}</span>
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {q.isPending && <Carregando linhas={4} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

      {q.isSuccess && (
        <Cartao titulo={fData(dia)} subtitulo="Um dia em aberto é o gestor que não fechou, nunca falta da pessoa">
          <div className="eop-indicadores">
            <Indicador rotulo="Combinados" valor={String(combinados.length)} />
            <Indicador rotulo="Fechados" valor={String(fechados.length)} />
            <Indicador rotulo="Cumpridos"
                       valor={String(fechados.filter((c) => c.resultado === "cumprido").length)} />
            <Indicador rotulo="Sem combinado" valor={String(semCombinado.length)} inverso
                       {...(semCombinado.length > 0 ? { detalhe: "combine o dia" } : {})} />
          </div>
        </Cartao>
      )}

      {q.isSuccess && combinados.length === 0 && semCombinado.length === 0 && (
        <EmptyState
          titulo="Ninguém na equipe ainda"
          descricao="Cadastre as pessoas e admita o vínculo. O combinado do dia aparece aqui em seguida."
        />
      )}

      {q.isSuccess && combinados.length > 0 && (
        <Cartao titulo="Do dia">
          <ul className="eop-op">
            {combinados.map((c) => {
              const itens = (Array.isArray(c.itens) ? c.itens : []) as Item[];
              const feitos = itens.filter((i) => i.entregue).length;
              return (
                <li key={c.id}
                    data-tom={c.resultado === "nao_cumprido" ? "critico"
                      : c.resultado === "parcial" ? "atencao" : undefined}>
                  <span aria-hidden className="eop-op-icone">
                    {c.resultado === "cumprido" ? <Check size={15} />
                      : c.resultado === "parcial" ? <Minus size={15} />
                      : c.resultado === "nao_cumprido" ? <TriangleAlert size={15} />
                      : <ClipboardCheck size={15} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="eop-op-titulo">
                      {c.nome}{c.projeto ? ` · ${c.projeto}` : ""}
                    </span>
                    <span className="eop-op-sub">
                      <span className="num">{feitos}</span> de{" "}
                      <span className="num">{itens.length}</span>
                      {c.fechadoEm
                        ? ` · fechado como ${c.resultado === "nao_cumprido"
                            ? "não cumprido" : c.resultado}`
                        : " · em aberto"}
                    </span>
                    {c.motivo && <span className="eop-op-explica">{c.motivo}</span>}
                  </span>
                  {!c.fechadoEm && (
                    <button className="botao h-7 px-2.5 text-xs" onClick={() => setFechando(c.id)}>
                      Fechar o dia
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </Cartao>
      )}

      {semCombinado.length > 0 && (
        <Cartao titulo="Ainda sem combinado" subtitulo="Combinar leva um minuto e é o que torna a conversa da noite objetiva">
          <ul className="eop-op">
            {semCombinado.map((m) => (
              <li key={m.vinculoId ?? m.nome}>
                <span aria-hidden className="eop-op-icone"><CircleDashed size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">{m.nome}</span>
                </span>
                <button className="botao h-7 px-2.5 text-xs"
                        onClick={() => setCombinandoPara(m.vinculoId ?? null)}>
                  <Plus aria-hidden size={13} /> Combinar
                </button>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {combinandoPara && (
        <FormCombinar vinculoId={combinandoPara} dia={dia}
                      aoFechar={() => setCombinandoPara(null)} />
      )}
      {fechando && (
        <FormFechar combinado={combinados.find((c) => c.id === fechando)!}
                    aoFechar={() => setFechando(null)} />
      )}
    </Pagina>
  );
}

/* ------------------------------------------------------------ combinar */

function FormCombinar(
  { vinculoId, dia, aoFechar }: { vinculoId: string; dia: string; aoFechar: () => void },
) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [projeto, setProjeto] = useState("");
  const [itens, setItens] = useState<Item[]>([{ chave: "i1", titulo: "" }]);

  const validos = itens.filter((i) => i.titulo.trim().length > 0);

  const salvar = useMutation({
    mutationFn: () => trpc.atividade.combinar.mutate({
      vinculoId, dia,
      projeto: projeto.trim() || null,
      itens: validos.map((i, n) => ({
        chave: `i${n + 1}`, titulo: i.titulo.trim(), entregue: false,
      })),
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["atividade"] }); aoFechar(); },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Modal titulo={`Combinar o dia ${fData(dia)}`} aoFechar={aoFechar}>
      {erro && (
        <p className="eop-erro" role="alert">
          <TriangleAlert aria-hidden size={15} /><span>{erro}</span>
        </p>
      )}
      <div className="eop-form">
        <label className="col-span-2">
          <span>Projeto</span>
          <input value={projeto} onChange={(e) => setProjeto(e.target.value)}
                 placeholder="EnturOS Pessoas" />
        </label>
      </div>

      <p className="carimbo mt-3">O escopo do dia, item a item.</p>
      <ul className="eop-itens">
        {itens.map((it, n) => (
          <li key={it.chave}>
            <input value={it.titulo} placeholder={`Item ${n + 1}`}
                   onChange={(e) => setItens((x) =>
                     x.map((y, i) => i === n ? { ...y, titulo: e.target.value } : y))} />
            {itens.length > 1 && (
              <button className="botao-icone" aria-label="Remover item"
                      onClick={() => setItens((x) => x.filter((_, i) => i !== n))}>
                <Trash2 aria-hidden size={15} />
              </button>
            )}
          </li>
        ))}
      </ul>
      <button className="botao h-7 px-2.5 text-xs mt-2"
              onClick={() => setItens((x) => [...x, { chave: `i${x.length + 1}`, titulo: "" }])}>
        <Plus aria-hidden size={13} /> Mais um item
      </button>

      <footer className="eop-modal-rodape">
        <button className="botao" onClick={aoFechar}>Cancelar</button>
        <button className="botao" data-variante="primario"
                disabled={salvar.isPending || validos.length === 0}
                onClick={() => { setErro(null); salvar.mutate(); }}>
          {salvar.isPending ? "Salvando" : "Combinar"}
        </button>
      </footer>
    </Modal>
  );
}

/* --------------------------------------------------------------- fechar */

function FormFechar(
  { combinado, aoFechar }:
  { combinado: { id: string; nome: string; dia: string; itens: unknown }; aoFechar: () => void },
) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [itens, setItens] = useState<Item[]>(
    (Array.isArray(combinado.itens) ? combinado.itens : []) as Item[]);
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");

  const feitos = itens.filter((i) => i.entregue).length;
  const sugerido = feitos === itens.length ? "cumprido"
    : feitos === 0 ? "nao_cumprido" : "parcial";
  const precisaMotivo = sugerido === "nao_cumprido";

  const salvar = useMutation({
    mutationFn: () => trpc.atividade.fechar.mutate({
      id: combinado.id,
      itens: itens.map((i) => ({
        chave: i.chave, titulo: i.titulo, entregue: i.entregue ?? false,
      })),
      motivo: motivo.trim() || null,
      observacao: observacao.trim() || null,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["atividade"] }); aoFechar(); },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Modal titulo={`Fechar o dia · ${combinado.nome}`} aoFechar={aoFechar}>
      {erro && (
        <p className="eop-erro" role="alert">
          <TriangleAlert aria-hidden size={15} /><span>{erro}</span>
        </p>
      )}

      <ul className="eop-itens">
        {itens.map((it, n) => (
          <li key={it.chave}>
            <label className="eop-check">
              <input type="checkbox" checked={it.entregue ?? false}
                     onChange={(e) => setItens((x) =>
                       x.map((y, i) => i === n ? { ...y, entregue: e.target.checked } : y))} />
              <span>{it.titulo}</span>
            </label>
          </li>
        ))}
      </ul>

      <p className="carimbo mt-3">
        Resultado sugerido: <strong>{sugerido === "nao_cumprido" ? "não cumprido" : sugerido}</strong>.
        Quem fecha é você; o sistema calcula e não decide.
      </p>

      {precisaMotivo && (
        <div className="eop-form mt-3">
          <label className="col-span-2">
            <span>Motivo</span>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="O que impediu a entrega" />
          </label>
        </div>
      )}

      <div className="eop-form mt-2">
        <label className="col-span-2">
          <span>Observação</span>
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </label>
      </div>

      <footer className="eop-modal-rodape">
        <button className="botao" onClick={aoFechar}>Cancelar</button>
        <button className="botao" data-variante="primario"
                disabled={salvar.isPending || (precisaMotivo && motivo.trim().length < 5)}
                onClick={() => { setErro(null); salvar.mutate(); }}>
          {salvar.isPending ? "Fechando" : "Fechar o dia"}
        </button>
      </footer>
    </Modal>
  );
}

/* ------------------------------------------------------------- política */

/**
 * A política e quem deu ciência.
 *
 * A ilicitude do monitoramento no Brasil mora na OCULTAÇÃO, não na medição.
 * Esta tela é o que transforma medir em lícito, e por isso ela existe antes
 * de qualquer coletor.
 */
export function PoliticaMonitoramento() {
  const q = useQuery({
    queryKey: ["atividade", "politica"],
    queryFn: () => trpc.atividade.politica.query(),
  });
  const fontes = useQuery({
    queryKey: ["atividade", "fontes"],
    queryFn: () => trpc.atividade.fontes.query(),
  });

  return (
    <Pagina
      secao="Atividade"
      titulo="Política de monitoramento"
      descricao="O texto vigente, o que ele autoriza coletar e quem deu ciência. Sem ciência assinada, o coletor não liga para aquela pessoa."
    >
      {q.isPending && <Carregando linhas={4} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

      {q.isSuccess && !q.data.vigente && (
        <EmptyState
          titulo="Nenhuma política publicada"
          descricao="Este é o passo A0 do plano. O texto precisa de revisão de advogado trabalhista antes de publicar, e nada é coletado até ele existir."
        />
      )}

      {q.isSuccess && q.data.vigente && (
        <>
          <Cartao titulo={`Versão ${q.data.vigente.versao}`}
                  subtitulo={`vigente desde ${fData(q.data.vigente.vigenciaDe)}`}>
            <dl className="eop-dados">
              <dt>Deram ciência</dt>
              <dd className="num">
                {q.data.vigente.cientes} de {q.data.vigente.equipe}
              </dd>
              <dt>Hash do texto</dt>
              <dd className="num text-xs">{q.data.vigente.textoHash.slice(0, 16)}</dd>
            </dl>
            <p className="carimbo mt-3">
              O hash é o que prova qual texto foi aceito. Editar a política gera
              versão nova e a ciência anterior deixa de valer, porque mudou o que
              se coleta.
            </p>
          </Cartao>

          {Array.isArray(q.data.vigente.escopo) && q.data.vigente.escopo.length > 0 && (
            <Cartao titulo="O que esta versão autoriza coletar"
                    subtitulo="Coletar além do declarado é a definição de monitoramento oculto">
              <ul className="eop-op">
                {(q.data.vigente.escopo as string[]).map((e, i) => (
                  <li key={i}>
                    <span aria-hidden className="eop-op-icone"><Check size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-op-titulo">{e}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}
        </>
      )}

      {fontes.isSuccess && (
        <Cartao titulo="Fontes" subtitulo="Nenhuma roda no notebook: todas leem a interface oficial da própria ferramenta">
          {fontes.data.length === 0 ? (
            <p className="carimbo">
              Nenhuma fonte configurada. É a onda A2 do plano, e ela vem depois
              da política.
            </p>
          ) : (
            <ul className="eop-op">
              {fontes.data.map((f) => (
                <li key={f.id} data-tom={f.ativa ? undefined : "atencao"}>
                  <span aria-hidden className="eop-op-icone"><Radio size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="eop-op-titulo">{f.rotulo}</span>
                    <span className="eop-op-sub">
                      {f.ativa ? "ativa" : "desligada"}
                      {f.ultimaColetaEm ? ` · última coleta ${fData(f.ultimaColetaEm.slice(0, 10))}` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      )}
    </Pagina>
  );
}
